/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, runInAction, observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon, Loader, Dimmer } from 'semantic-ui-react';
import c from 'classnames';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import { swallowError, mapToArray } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreLoading, isStoreError, isStoreEmpty } from '@amzn/base-ui/dist/models/BaseStore';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';

import Logos from './Logos';
import BlankImageIcons from './BlankImageIcons';
import FloatingNavBar from './FloatingNavBar';
import StepGuideCard from '../../helpers/StepGuideCard';
import { enableWSWizardV2 } from '../../../helpers/settings';
import { isDeprecatedName } from '../../../helpers/env-type-display';

// expected props
// - onPrevious (via props)
// - onNext (via props) a function is called with the selected envTypeId
// - envTypesStore (via injection)
// - userStore (via injection)
class SelectEnvTypeStep extends React.Component {
  constructor(props) {
    super(props);
    this.topBarRef = React.createRef();
    this.listRef = React.createRef();
    runInAction(() => {
      this.listAtBottom = false;
      this.selectedEnvTypeId = undefined;
      this.processing = false;

      const envTypes = this.envTypesStore.listApproved || [];
      // Deprecated (retired) types are only hidden for researchers: admins need to see them to
      // manage upgrades, but an admin previewing "Researcher View" should see exactly what a
      // researcher would, so it is hidden there too.
      const visibleEnvTypes = this.hideDeprecated ? _.filter(envTypes, e => !isDeprecatedName(e.name)) : envTypes;
      const sortedEnvTypes = _.sortBy(mapToArray(visibleEnvTypes), e => e.name);
      const groupedEnvTypes = _.groupBy(sortedEnvTypes, 'category');

      // eslint-disable-next-line prefer-const
      let { 'Blank Images': blankImages, ...CustomImages } = groupedEnvTypes;
      // V2 lifts "Blank Images" out into its own Quick Start sidebar. V1 has no sidebar and
      // presents every category as a tab, so keeping that split would drop the blank images
      // off the page altogether.
      const categoryGroups = enableWSWizardV2 ? CustomImages : groupedEnvTypes;
      const sortImgs = ['windows', 'ubuntu', 'amazon', 'redhat', 'jupyter'];

      // Sorted in place, so the V1 "Blank Images" tab gets this OS ordering too rather than
      // the plain alphabetical order it would otherwise inherit.
      if (blankImages) {
        blankImages.sort((a, b) => {
          const aIndex = sortImgs.findIndex(e => a.name.toLowerCase().includes(e));
          const bIndex = sortImgs.findIndex(e => b.name.toLowerCase().includes(e));
          return aIndex - bIndex;
        });
      }

      const sortedCategories = _.sortBy(Object.keys(categoryGroups), category => {
        if (category === '') return String.fromCharCode(-1);
        if (category === 'Other') return String.fromCharCode(-2);
        return category.toLowerCase();
      });
      this.groupedEnvTypes = categoryGroups;
      this.blankImages = blankImages;
      this.sortedCategories = sortedCategories;
      this.selectedCategory = sortedCategories.length ? sortedCategories[0] : null;
    });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    swallowError(this.envTypesStore.load());
    // Check initial scroll state after render
    setTimeout(() => this.checkListScroll(), 0);
  }

  goto(pathname) {
    const goto = gotoFn(this);
    goto(pathname);
  }

  get userStore() {
    return this.props.userStore;
  }

  get envTypesStore() {
    return this.props.envTypesStore;
  }

  // Deprecated types/configs stay visible to admins (they need to see them to manage upgrades),
  // unless the admin has toggled on "Researcher View" to preview what a researcher sees.
  get hideDeprecated() {
    const isAdmin = _.get(this.userStore, 'user.isAdmin', false);
    const researcherView = _.get(this.userStore, 'researcherView', false);
    return !isAdmin || researcherView;
  }

  get displayingTypes() {
    return this.groupedEnvTypes[this.selectedCategory];
  }

  selectCategory(categoryName) {
    runInAction(() => {
      this.selectedCategory = categoryName;
      this.selectedEnvTypeId = undefined;
      this.listAtBottom = true;
    });
    // Re-check scroll state after category switch
    setTimeout(() => this.checkListScroll(), 0);
  }

  handleSelectedEnvType = typeId => {
    this.selectedEnvTypeId = typeId;
  };

  checkListScroll = () => {
    const el = this.listRef.current;
    if (!el) return;
    const isScrollable = el.scrollHeight > el.clientHeight;
    const atBottom = !isScrollable || (el.scrollHeight - el.scrollTop - el.clientHeight < 10);
    if (atBottom !== this.listAtBottom) {
      runInAction(() => { this.listAtBottom = atBottom; });
    }
  };

  handleListScroll = () => {
    this.checkListScroll();
  };

  handlePrevious = event => {
    event.preventDefault();
    event.stopPropagation();
    if (_.isFunction(this.props.onPrevious)) this.props.onPrevious();
  };

  async processNext(typeId = null) {
    if (_.isFunction(this.props.onNext)) {
      try {
        this.processing = true;
        await this.props.onNext(typeId || this.selectedEnvTypeId);
      } catch (error) {
        displayError(error);
      } finally {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  handleNext = async event => {
    event.preventDefault();
    event.stopPropagation();
    this.processNext();
  };

  render() {
    const store = this.envTypesStore;
    if (!store) return null;

    let content;
    if (isStoreError(store)) {
      content = this.renderLoadingError();
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder className="mt2" />;
    } else if (isStoreEmpty(store)) {
      content = this.renderEmpty();
    } else if (enableWSWizardV2) {
      content = this.renderContentV2();
    } else {
      content = this.renderContentV1();
    }

    return content;
  }

  // Legacy workspace type picker, shown when enableWSWizardV2 is false. Every category is a
  // tab and the types in the active tab are shown as cards. A single click on a card goes
  // straight to the configure step, so there is no Next button in either nav bar.
  renderContentV1() {
    return (
      <div className="envtype-page animated fadeIn">
        {this.renderButtons({ hideNext: true })}
        {this.renderGuideCardV1()}
        {this.renderFloatingNav({ hideNext: true })}

        <Dimmer active={this.processing} inverted>
          <Loader />
        </Dimmer>

        <div className="envtype-main">
          <div className="envtype-main-title">Workspace Types</div>
          <div className="envtype-main-subtitle">
            Pick a category, then choose a workspace type. Selecting one takes you straight to configuration.
          </div>
          {this.renderCategoryTabs()}
          {this.renderCards()}
        </div>
      </div>
    );
  }

  renderContentV2() {
    const nextDisabled = _.isUndefined(this.selectedEnvTypeId);

    return (
      <div className="envtype-page animated fadeIn">
        {this.renderButtons({ nextDisabled })}
        {this.renderGuideCard()}
        {this.renderFloatingNav({ nextDisabled })}

        <Dimmer active={this.processing} inverted>
          <Loader />
        </Dimmer>

        <div className="envtype-layout">
          {/* Left sidebar — Quick Start blank images */}
          <div className="envtype-sidebar">
            <div className="envtype-sidebar-title">Quick Start</div>
            <div className="envtype-sidebar-subtitle">Start with a blank image</div>
            <div className="envtype-sidebar-divider" />
            <div className="envtype-sidebar-list">
              {_.map(this.blankImages, type => (
                <button
                  key={type.id}
                  type="button"
                  className="envtype-quickstart-btn"
                  onClick={() => this.processNext(type.id)}
                  disabled={this.processing}
                >
                  <BlankImageIcons name={type.name} />
                  <span className="envtype-quickstart-label">{type.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right panel — Custom images with tabs */}
          <div className="envtype-main">
            <div className="envtype-main-title">Software Bundles</div>
            <div className="envtype-main-subtitle">Pre-configured workspaces with popular research tools — just launch and start working.</div>
            {this.renderCategoryTabs()}
            {this.renderTypeList()}
          </div>
        </div>
      </div>
    );
  }

  renderCategoryTabs() {
    return (
      <div className="study-tabs">
        {_.map(this.sortedCategories, category => {
          const isActive = this.selectedCategory === category;
          const count = (this.groupedEnvTypes[category] || []).length;
          return (
            <button
              key={category}
              type="button"
              className={c('study-tab', { active: isActive })}
              onClick={() => this.selectCategory(category)}
            >
              <Logos.Dynamic name={category} size="" className="envtype-tab-icon" />
              {/* Types can carry an empty category, which would otherwise render a nameless tab */}
              <span>{category || 'Uncategorised'}</span>
              {count > 0 && <span className="study-tab-count">{count}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  renderTypeList() {
    const types = this.displayingTypes || [];
    if (_.isEmpty(types)) {
      return (
        <div className="envtype-empty-category">
          No workspace types available in this category.
        </div>
      );
    }

    return (
      <div className={c('envtype-list-wrapper', { 'scrolled-to-bottom': this.listAtBottom })}>
        <div
          className="envtype-list"
          ref={this.listRef}
          onScroll={this.handleListScroll}
        >
          {_.map(types, type => {
            const isSelected = this.selectedEnvTypeId === type.id;
            return (
              <button
                key={type.id}
                type="button"
                className={c('envtype-row', { selected: isSelected })}
                onClick={() => this.handleSelectedEnvType(type.id)}
                onDoubleClick={() => this.processNext(type.id)}
                disabled={this.processing}
              >
                <div className="envtype-row-radio">
                  <span className={c('envtype-radio-dot', { checked: isSelected })} />
                </div>
                <div className="envtype-row-icon">
                  <Logos.Dynamic name={type.name} size="large" />
                </div>
                <div className="envtype-row-info">
                  <div className="envtype-row-name">{type.name}</div>
                  {/* eslint-disable-next-line react/no-danger */}
                  <div className="envtype-row-desc" dangerouslySetInnerHTML={{ __html: type.descHtml }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Card grid for the active category in the V1 layout. Clicking a card advances straight to
  // the configure step, so the cards carry no selected state.
  renderCards() {
    const types = this.displayingTypes || [];
    if (_.isEmpty(types)) {
      return <div className="envtype-empty-category">No workspace types available in this category.</div>;
    }

    return (
      <div className="envtype-card-grid">
        {_.map(types, type => (
          <button
            key={type.id}
            type="button"
            data-testid="env-type-card"
            className="envtype-card"
            onClick={() => this.processNext(type.id)}
            disabled={this.processing}
          >
            <span className="envtype-card-accent" />
            <span className="envtype-card-body">
              <span className="envtype-card-head">
                <span className="envtype-card-icon">
                  <Logos.Dynamic name={type.name} size="large" color="var(--rmit-blue)" />
                </span>
                <span className="envtype-card-name">{type.name}</span>
              </span>
              {/* Yes, we are doing dangerouslySetInnerHTML, the content was already sanitized by showdownjs */}
              {/* eslint-disable-next-line react/no-danger */}
              <span className="envtype-card-desc" dangerouslySetInnerHTML={{ __html: type.descHtml }} />
            </span>
            <span className="envtype-card-cta">
              Select <Icon name="arrow right" />
            </span>
          </button>
        ))}
      </div>
    );
  }

  renderLoadingError() {
    const store = this.envTypesStore;
    return (
      <>
        <ErrorBox error={store.error} className="p0 mt2 mb3" />
        {this.renderButtons({ hideNext: true })}
      </>
    );
  }

  renderEmpty() {
    return (
      <>
        <div className="workspace-empty-state mt2">
          <div className="workspace-empty-state-icon">
            <Icon name="server" />
          </div>
          <div className="workspace-empty-state-title">No workspace types</div>
          <div className="workspace-empty-state-subtitle">
            There are no workspace types to choose from. Your role might be restricted. Please contact your
            administrator.
          </div>
        </div>
        {this.renderButtons({ hideNext: true })}
      </>
    );
  }

  // hideNext is used by the V1 layout, where a card click advances on its own.
  renderButtons({ nextDisabled = true, hideNext = false } = {}) {
    const processing = this.processing;
    return (
      <div className="setup-top-bar" ref={this.topBarRef}>
        <div className="setup-top-bar-left">
          <span className="setup-top-bar-title">
            <Icon name="computer" /> Select Software
          </span>
        </div>
        <div className="setup-top-bar-nav">
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            disabled={processing}
            onClick={this.props.onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            disabled={processing}
            onClick={this.handlePrevious}
          >
            <Icon name="arrow left" /> Back
          </button>
          {hideNext || (
            <button
              type="button"
              className="workspace-action-btn workspace-action-btn-primary"
              disabled={nextDisabled || processing}
              onClick={this.handleNext}
            >
              {processing ? <Loader size="tiny" active inline /> : <>Next <Icon name="arrow right" /></>}
            </button>
          )}
        </div>
        <div className="setup-top-bar-description">
          Choose a blank image or a pre-bundled software image for your workspace.
        </div>
      </div>
    );
  }

  renderGuideCard() {
    return (
      <StepGuideCard guideId="step-select-software" title="Choosing your software">
        On this page, you can choose between a <strong>Quick Start</strong> blank image on the
        left, which gives you a clean operating system with nothing pre-installed. Alternatively, choose
        a <strong>Software Bundle</strong> from the list, which comes with popular research
        tools already set up and ready to use. If you are unsure, a Quick Start image is a
        good place to begin and you can install your own software later.
      </StepGuideCard>
    );
  }

  // Separate copy and guideId from renderGuideCard, since the V2 wording describes the
  // Quick Start sidebar and Software Bundle list, neither of which exists in the V1 layout.
  renderGuideCardV1() {
    return (
      <StepGuideCard guideId="step-select-software-v1" title="Choosing your software">
        Use the tabs to browse the categories of workspace type available to you. <strong>Blank Images</strong> give you
        a clean operating system with nothing pre-installed, while the other categories come with research tools already
        set up. Click a card to select that type and move on to configuration. If you are unsure, a blank image is a
        good place to begin and you can install your own software later.
      </StepGuideCard>
    );
  }

  renderFloatingNav({ nextDisabled = true, hideNext = false } = {}) {
    const processing = this.processing;
    return (
      <FloatingNavBar sentinelRef={this.topBarRef}>
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-secondary"
          disabled={processing}
          onClick={this.props.onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-secondary"
          disabled={processing}
          onClick={this.handlePrevious}
        >
          <Icon name="arrow left" /> Back
        </button>
        {hideNext || (
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-primary"
            disabled={nextDisabled || processing}
            onClick={this.handleNext}
          >
            {processing ? <Loader size="tiny" active inline /> : <>Next <Icon name="arrow right" /></>}
          </button>
        )}
      </FloatingNavBar>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(SelectEnvTypeStep, {
  handlePrevious: action,
  handleNext: action,
  handleSelectedEnvType: action,
  handleListScroll: action,
  processNext: action,
  groupedEnvTypes: observable,
  blankImages: observable,
  sortedCategories: observable,
  selectedCategory: observable,
  listAtBottom: observable,
  displayingTypes: computed,
  userStore: computed,
  envTypesStore: computed,
  hideDeprecated: computed,
  processing: observable,
  selectedEnvTypeId: observable,
});

export default inject('userStore', 'envTypesStore')(withRouter(observer(SelectEnvTypeStep)));
